import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useRecipeById } from '@/hooks/useRecipeById';
import { useRecipeForm } from '@/hooks/useRecipeForm';
import { Skeleton } from "@/components/ui/skeleton";
import { PageLayout } from '@/components/layout';
import RecipeForm from '@/components/recipe/RecipeForm';
import QuickRecipeForm from '@/components/recipe/QuickRecipeForm';

const EditRecipePage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: recipe, isLoading, refetch } = useRecipeById(id);
  
  // Detect if this is a quick recipe
  const isQuickRecipe = recipe?.isQuickRecipe || recipe?.tags?.includes('quick-meal') || false;

  const {
    isSubmitting,
    isProcessingImage,
    handleSubmit
  } = useRecipeForm(recipe, () => {
    navigate(`/recipe/${id}`);
  });

  const handleCancel = () => {
    navigate(`/recipe/${id}`);
  };

  const handleRecipeUpdated = () => {
    refetch();
    navigate(`/recipe/${id}`);
  };

  if (isLoading) {
    return (
      <PageLayout 
        title="Loading Recipe..." 
        description="Please wait while we fetch the recipe details"
      >
        <div className="space-y-4">
          <Skeleton className="h-10 w-3/4" />
          <Skeleton className="h-4 w-1/4" />
          <Skeleton className="h-20 w-full" />
          <div className="grid grid-cols-3 gap-4">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
          <Skeleton className="h-60 w-full" />
        </div>
      </PageLayout>
    );
  }

  if (!recipe) {
    return (
      <PageLayout 
        title="Recipe Not Found" 
        description="The recipe you're looking for doesn't exist"
      >
        <div className="text-center py-12">
          <p className="text-muted-foreground mb-6">Please check the URL or try again.</p>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout 
      title={`Edit ${isQuickRecipe ? 'Quick Recipe' : 'Recipe'}`}
      description={`Editing ${recipe.title}`}
    >
      <div className="container py-8">
        {isQuickRecipe ? (
          <QuickRecipeForm 
            recipe={recipe} 
            onRecipeUpdated={handleRecipeUpdated} 
          />
        ) : (
          <RecipeForm 
            recipe={recipe} 
            onSubmit={handleSubmit} 
            onCancel={handleCancel} 
            isLoading={isSubmitting}
            isProcessingImage={isProcessingImage}
          />
        )}
      </div>
    </PageLayout>
  );
};

export default EditRecipePage;